import { Component, ElementRef, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute, NavigationEnd, RouterOutlet } from '@angular/router';

import { LoadingBarModule } from '@ngx-loading-bar/core';
import { MatSidenavModule } from '@angular/material/sidenav';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { CommonModule } from '@angular/common';

// Bootstrap's `lg`. The sidebar SCSS uses the same breakpoint, so the two must
// stay in step.
const SMALL_WIDTH_BREAKPOINT = 992;

export interface Options {
  heading?: string;
  removeFooter?: boolean;
}

@Component({
    selector: 'app-admin',
    templateUrl: './admin-layout.component.html',
    styleUrls: ['./admin-layout.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterOutlet, MatSidenavModule, LoadingBarModule, HeaderComponent, SidebarComponent, FooterComponent]
})
//

export class AdminLayoutComponent implements OnInit, OnDestroy {
  private _router: Subscription  = {} as Subscription;

  /**
   * Single source of truth for the drawer. `isOver` comes from one breakpoint
   * observer; `sidenavOpened` is kept in sync with the drawer through
   * `(openedChange)` so a programmatic close can never leave the binding and the
   * drawer disagreeing — that desync is what used to leave a 250px blank column.
   */
  readonly isOver = signal(false);
  readonly sidenavOpened = signal(true);

  routeOptions = signal<Options>({} as Options);

  options = {
    lang: 'en',
    theme: 'winter',
    settings: false,
    docked: false,
    boxed: false,
    opened: false
  };

  currentLang = 'en';

  constructor(
    private _element: ElementRef,
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    breakpointObserver: BreakpointObserver
  ) {
    breakpointObserver
      .observe(`(max-width: ${SMALL_WIDTH_BREAKPOINT - 0.02}px)`)
      .pipe(takeUntilDestroyed())
      .subscribe(({ matches }) => {
        this.isOver.set(matches);
        this.sidenavOpened.set(!matches);
      });
  }

  ngOnInit(): void {
    this._router = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Scroll to top on view load
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
          mainContent.scrollTop = 0;
        }
        this.closeSidenavIfOver();
        this.runOnRouteChange();
      });

    this.runOnRouteChange();
  }

  ngOnDestroy() {
    this._router.unsubscribe();
  }

  runOnRouteChange(): void {
    this.route.children.forEach((route: ActivatedRoute) => {
      let activeRoute: ActivatedRoute = route;
      while (activeRoute.firstChild) {
        activeRoute = activeRoute.firstChild;
      }
      this.routeOptions.set(activeRoute.snapshot.data);
    });

    if (this.routeOptions().hasOwnProperty('heading')) {
      this.setTitle(this.routeOptions().heading as string);
    }
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(
      'CORE-APP | ' + newTitle
    );
  }

  toggleSidenav(): void {
    this.sidenavOpened.update(opened => !opened);
  }

  receiveMessage($event : any) {
    this.options = $event;
  }

  toggleFullscreen(): void {
    const elem = this._element.nativeElement.querySelector('.main-content');
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullScreen) {
      elem.webkitRequestFullScreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.msRequestFullScreen) {
      elem.msRequestFullScreen();
    }
  }

  /**
   * Close sidenav when menu item is clicked on mobile
   */
  onMenuClick(): void {
    this.closeSidenavIfOver();
  }

  private closeSidenavIfOver(): void {
    if (this.isOver()) {
      this.sidenavOpened.set(false);
    }
  }
}
